import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function Shell({ title, sub, children }) {
  return (
    <div className="grain relative flex min-h-[80vh] items-center justify-center px-4 py-12">
      <div className="absolute right-0 top-10 h-72 w-72 rounded-full bg-primary/20 blur-[100px]" />
      <div className="card-surface relative w-full max-w-md p-7 sm:p-9 fade-up">
        <Logo testid="auth-logo" />
        <h1 className="mt-8 text-2xl font-bold sm:text-3xl">{title}</h1>
        {sub && <p className="mt-2 text-sm text-muted-foreground">{sub}</p>}
        <div className="mt-7">{children}</div>
      </div>
    </div>
  );
}

const Field = ({ label, id, ...p }) => (
  <div className="space-y-2"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`${id}-input`} className="h-11" required {...p} /></div>
);

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await fn(); } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };
  return { busy, error, run };
}

const Submit = ({ busy, children, testid }) => (
  <Button type="submit" className="h-11 w-full" disabled={busy} data-testid={testid}>{busy ? <Loader2 className="animate-spin" /> : children}</Button>
);
const ErrorText = ({ error }) => error ? <p className="text-sm text-rose-500" data-testid="auth-error">{error}</p> : null;

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, error, run } = useSubmit(async () => { const u = await login(f.email, f.password); toast.success(`Welcome back, ${u.name.split(" ")[0]}`); nav(sp.get("next") || "/"); });
  return (
    <Shell title="Welcome back" sub="Log in to buy, sell and download templates.">
      <form onSubmit={run} className="space-y-4">
        <Field label="Email" id="login-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password" id="login-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="text-right"><Link to="/forgot-password" className="text-xs text-primary" data-testid="forgot-password-link">Forgot password?</Link></div>
        <ErrorText error={error} />
        <Submit busy={busy} testid="login-submit-btn">Log in</Submit>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">New to GrafixMarket? <Link to="/register" className="font-semibold text-primary" data-testid="go-register-link">Create an account</Link></p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const { busy, error, run } = useSubmit(async () => { await register(f.name, f.email, f.password); toast.success("Account created! Check your email to verify."); nav("/"); });
  return (
    <Shell title="Create your account" sub="Join designers and buyers across Africa and beyond.">
      <form onSubmit={run} className="space-y-4">
        <Field label="Full name" id="register-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="Email" id="register-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password (min 8 characters)" id="register-password" type="password" minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <ErrorText error={error} />
        <Submit busy={busy} testid="register-submit-btn">Sign up</Submit>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-semibold text-primary" data-testid="go-login-link">Log in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit(async () => { await api.post("/auth/forgot-password", { email }); setSent(true); });
  return (
    <Shell title="Reset your password" sub="We'll email you a secure reset link.">
      {sent ? <p className="text-sm" data-testid="forgot-sent-msg">If an account exists for <b>{email}</b>, a reset link is on its way. It expires in 1 hour.</p> : (
        <form onSubmit={run} className="space-y-4">
          <Field label="Email" id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <ErrorText error={error} />
          <Submit busy={busy} testid="forgot-submit-btn">Send reset link</Submit>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-primary">Back to login</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const { busy, error, run } = useSubmit(async () => { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated. Please log in."); nav("/login"); });
  return (
    <Shell title="Choose a new password">
      <form onSubmit={run} className="space-y-4">
        <Field label="New password" id="reset-password" type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} />
        <ErrorText error={error} />
        <Submit busy={busy} testid="reset-submit-btn">Update password</Submit>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("loading");
  useEffect(() => {
    api.post("/auth/verify-email", { token: sp.get("token") }).then(() => { setState("ok"); refreshUser(); }).catch(() => setState("fail"));
  }, []); // eslint-disable-line
  return (
    <Shell title="Email verification">
      <div className="text-center" data-testid="verify-email-result">
        {state === "loading" && <Loader2 className="mx-auto animate-spin text-primary" />}
        {state === "ok" && <><CheckCircle2 className="mx-auto text-emerald-500" size={44} /><p className="mt-4">Your email is verified.</p><Button asChild className="mt-6"><Link to="/explore">Explore templates</Link></Button></>}
        {state === "fail" && <><XCircle className="mx-auto text-rose-500" size={44} /><p className="mt-4">This link is invalid or expired. Request a new one from your profile.</p></>}
      </div>
    </Shell>
  );
}
